import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
} from "@tanstack/react-router";
import {
	Calendar,
	CalendarCheck01,
	Hash01,
	Minimize01,
	Trash01,
} from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import { AssetSidebarDetails } from "@/components/Organisms/AssetSidebarDetails";
import { DeleteAssetModal } from "@/components/Organisms/DeleteAssetModal/DeleteAssetModal";
import { DetailsState } from "@/components/Organisms/Details/Details";
import {
	PageHeader,
	type PageHeaderTab,
} from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { Sidebar } from "@/components/Organisms/Sidebar/Sidebar";
import { AssetDetailsTab } from "@/features/assets/components";
import {
	AssetFormSaveProvider,
	useAssetFormSaveRegistry,
} from "@/features/assets/components/asset-form-save-context";
import { useAssetFormNavigationGuard } from "@/features/assets/hooks/use-asset-form-navigation-guard";
import { getAssetDisplayTitle } from "@/features/assets/lib/get-asset-display-title.util";
import {
	getOrgAssetDrawerDetailsPath,
	getOrgAssetWorkspacePath,
	getOrgAssetWorkspaceTabId,
	isNestedRecordUnderOrgAssetWorkspacePath,
} from "@/features/assets/lib/get-org-asset-workspace-path.util";
import {
	commitAutosaveOrStay,
	JOB_DETAILS_SAVE_FAIL_LABEL_KEYS,
	useDetailsSaveFailLabels,
} from "@/features/entity-details";
import {
	AssetWorkspaceTab,
	type AssetWorkspaceTabId,
} from "@/features/jobs/lib/get-asset-workspace-tab-id.util";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { assetInstanceQueryOptions } from "@/services/asset";
import { authQueryOptions } from "@/services/auth";
import { cx } from "@/utils/cx";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

/**
 * Top-level org Asset workspace shell — PageHeader tabs + tertiary sidebar details.
 */
export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/assets/(asset)/$assetId/workspace",
)({
	component: OrgAssetWorkspaceLayout,
});

function OrgAssetWorkspaceLayout() {
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});

	if (isNestedRecordUnderOrgAssetWorkspacePath(pathname)) {
		return <Outlet />;
	}

	return (
		<AssetFormSaveProvider>
			<OrgAssetWorkspaceLayoutInner />
		</AssetFormSaveProvider>
	);
}

function OrgAssetWorkspaceLayoutInner() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/assets/(asset)/$assetId/workspace",
	});
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const selectedTabId = getOrgAssetWorkspaceTabId(pathname);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isSidebarOpen, setIsSidebarOpen] = useState(true);

	const formSaveRegistry = useAssetFormSaveRegistry();
	const saveFailLabels = useDetailsSaveFailLabels(
		JOB_DETAILS_SAVE_FAIL_LABEL_KEYS,
	);
	useAssetFormNavigationGuard();

	const runWithFormCommit = useCallback(
		async (action: () => void) => {
			const ctrl = formSaveRegistry?.getController() ?? null;
			if (ctrl?.hasPending()) {
				const shouldStay = await commitAutosaveOrStay(
					ctrl.commitPending,
					saveFailLabels,
				);
				if (shouldStay) return;
			}
			action();
		},
		[formSaveRegistry, saveFailLabels],
	);

	const assetQuery = useQuery(
		assetInstanceQueryOptions.detail(params.assetId, {
			organizationId: params.orgId,
		}),
	);

	const canDeleteAsset = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteAsset",
			entityType: "Asset",
			entityId: params.assetId,
		}),
		enabled: !!params.assetId,
	});

	const title = getAssetDisplayTitle({
		name: assetQuery.data?.name,
		assetNumber: assetQuery.data?.assetNumber,
	});

	const tabs: PageHeaderTab[] = useMemo(
		() => [
			{
				id: AssetWorkspaceTab.Form,
				label: t("tickets.landing.sections.form"),
			},
			{
				id: AssetWorkspaceTab.Records,
				label: t("main.tickets.workspace.tabs.records"),
			},
			{
				id: AssetWorkspaceTab.Attachments,
				label: t("tickets.landing.sections.attachments"),
			},
			{
				id: AssetWorkspaceTab.Notes,
				label: t("tickets.landing.sections.notes"),
			},
			{
				id: AssetWorkspaceTab.Map,
				label: t("tickets.landing.sections.map"),
			},
		],
		[t],
	);

	const metadata = useMemo(() => {
		const asset = assetQuery.data;
		if (!asset) return [];

		const items: {
			label: string;
			icon: typeof Hash01;
		}[] = [];

		if (asset.assetNumber != null) {
			items.push({
				label: String(asset.assetNumber),
				icon: Hash01,
			});
		}
		if (asset.createdAt) {
			items.push({
				label: createdMetadataLabel(t, asset.createdAt),
				icon: Calendar,
			});
		}
		if (asset.updatedAt) {
			items.push({
				label: updatedMetadataLabel(t, asset.updatedAt),
				icon: CalendarCheck01,
			});
		}
		return items;
	}, [assetQuery.data, t]);

	const handleBreadcrumbAssets = () => {
		void runWithFormCommit(() => {
			navigate({
				to: "/web/$orgId/assets",
				params: { orgId: params.orgId },
				search: {},
			});
		});
	};

	const handleCollapseWorkspace = () => {
		void runWithFormCommit(() => {
			navigate({
				href: getOrgAssetDrawerDetailsPath({
					orgId: params.orgId,
					assetId: params.assetId,
				}),
			});
		});
	};

	const handleTabChange = (tabId: string) => {
		const next = tabId as AssetWorkspaceTabId;
		if (next === selectedTabId) return;

		void runWithFormCommit(() => {
			navigate({
				href: getOrgAssetWorkspacePath({
					orgId: params.orgId,
					assetId: params.assetId,
					tab: next,
				}),
			});
		});
	};

	const handleDeleteSuccess = () => {
		setIsDeleteModalOpen(false);
		void navigate({
			to: "/web/$orgId/assets",
			params: { orgId: params.orgId },
			search: {},
			replace: true,
		});
	};

	const sidebarLabel = t(
		"main.tickets.workspace.assets.workspace.sidebarLabel",
	);

	const sidebarTabs = useMemo(
		() => [
			{
				label: t("main.tickets.workspace.assets.drawer.tabs.details.label"),
				id: AssetDetailsTab.Details,
			},
			{
				label: t("main.tickets.workspace.assets.drawer.tabs.activity.label"),
				id: AssetDetailsTab.Activity,
			},
		],
		[t],
	);

	return (
		<div className="relative flex min-h-0 w-full flex-1 overflow-hidden">
			<div
				className={cx(
					"flex min-h-0 min-w-0 flex-1 flex-col transition-[padding] duration-300 ease-in-out motion-reduce:transition-none",
					isSidebarOpen && "lg:pr-[440px]",
				)}
			>
				<PageLayout
					header={
						<PageHeader
							type="asset"
							title={title}
							breadcrumbType="single"
							breadcrumbs={[
								{
									label: t("main.nav.items.assets.label"),
									onPress: handleBreadcrumbAssets,
								},
							]}
							metadata={metadata.length > 0 ? metadata : undefined}
							overflowAriaLabel={t("common.tableActions.options")}
							overflowActions={
								canDeleteAsset.data !== false
									? [
											{
												label: t(
													"main.tickets.workspace.assets.drawer.delete.action",
												),
												icon: Trash01,
												onPress: () => setIsDeleteModalOpen(true),
											},
										]
									: []
							}
							actions={[
								{
									label: t("main.tickets.workspace.action"),
									"aria-label": t(
										"main.tickets.workspace.assets.workspace.collapse",
									),
									icon: Minimize01,
									color: "secondary",
									onPress: handleCollapseWorkspace,
								},
							]}
							showTabs
							tabs={tabs}
							selectedTabId={selectedTabId}
							onTabChange={handleTabChange}
						/>
					}
				>
					<Outlet />
				</PageLayout>
			</div>
			<Sidebar
				isOpen={isSidebarOpen}
				onOpenChange={setIsSidebarOpen}
				aria-label={sidebarLabel}
				className="max-lg:hidden"
			>
				<AssetSidebarDetails
					orgId={params.orgId}
					assetId={params.assetId}
					state={
						assetQuery.isPending ? DetailsState.Loading : DetailsState.Default
					}
					tabs={sidebarTabs}
					data-testid="org-asset-workspace-sidebar-details"
				/>
			</Sidebar>
			<DeleteAssetModal
				isOpen={isDeleteModalOpen}
				setIsOpen={setIsDeleteModalOpen}
				handleClose={() => setIsDeleteModalOpen(false)}
				onDeleteSuccess={handleDeleteSuccess}
				assetId={params.assetId}
				organizationId={params.orgId}
			/>
		</div>
	);
}
